import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import AnimusHeroChrome from "../components/AnimusHeroChrome";
import { apiGet } from "../api/client";
import { useTwitchLive } from "../hooks/useTwitchLive";
import { splitHeadingLastWord } from "../utils/splitHeading";
import { streamPagePath } from "./stream/StreamPage";
import "../styles.css";

export default function Stream() {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isLive, streamInfo, loading: twitchLoading } = useTwitchLive("zemaxi");

  useEffect(() => {
    apiGet('/pages/stream')
      .then(setPage)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const c = page?.content || {};
  const seo = page?.seo || {};
  const offlineHeading = splitHeadingLastWord(c.offlineHeading, 'Currently ', 'Offline');
  const platformHeading = splitHeadingLastWord(c.platformHeading, 'Watch ', 'Live');
  const liveHeading = splitHeadingLastWord(c.platformHeading, 'Watch ', 'Live');
  const twitchParent = typeof window !== 'undefined' ? window.location.hostname : 'zemaxi.com';

  if (loading) {
    return (
      <>
        <Navbar />
        <main className="container" style={{ padding: '4rem 0' }}>
          <div className="video-loading"><div className="spinner"></div></div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO
        title={page?.title || "Live Stream"}
        description={seo.description || ""}
        keywords={seo.keywords || ""}
        url={seo.url || "https://zemaxi.com/stream"}
      />
      <Navbar />
      <main>
        <section className="stream-hero animus-hero animus-hero-bg">
          <AnimusHeroChrome />
          <div className="container">
            <p className="animus-hero-id font-mono">{'// SECTION_STREAM'}</p>
            <h1>Live <span className="highlight">Stream</span></h1>
            <p>{c.heroSubtitle || ''}</p>
          </div>
        </section>
        <section className="stream-section page-body-section">
          <div className="container">
            <div className="stream-container">
              {twitchLoading ? (
                <div className="offline-message stream-live-status stream-live-status--sync">
                  <i className="fas fa-sync fa-spin"></i>
                  <h2>Checking <span className="highlight">Stream</span> status…</h2>
                  <p>Connecting to Twitch…</p>
                </div>
              ) : isLive ? (
                <div className="stream-live-block">
                  <h2>{liveHeading.before}<span className="highlight">{liveHeading.highlight}</span></h2>
                  {streamInfo?.title && <p className="stream-live-meta"><strong>{streamInfo.title}</strong></p>}
                  {streamInfo?.gameName && (
                    <p className="stream-live-meta font-mono">
                      {streamInfo.gameName}
                      {streamInfo.viewerCount > 0 && ` · ${streamInfo.viewerCount.toLocaleString()} viewers`}
                    </p>
                  )}
                  <div className="twitch-embed">
                    <iframe
                      src={`https://player.twitch.tv/?channel=zemaxi&parent=${encodeURIComponent(twitchParent)}&parent=zemaxi.com`}
                      title="ZeMaxi live on Twitch"
                      allowFullScreen
                    />
                  </div>
                </div>
              ) : (
                <div className="offline-message">
                  <i className="fas fa-broadcast-tower"></i>
                  <h2>{offlineHeading.before}<span className="highlight">{offlineHeading.highlight}</span></h2>
                  <p>{c.offlineText || ''}</p>
                  <p>
                    In the meantime, you can
                    {(c.offlineLinks || []).map((link, i, arr) => {
                      const sep = i === 0 ? ' ' : i === arr.length - 1 ? ' or ' : ', ';
                      return (
                        <span key={link.href}>
                          {sep}
                          {link.href.startsWith('/') ? (
                            <Link to={link.href}>{link.label}</Link>
                          ) : (
                            <a href={link.href}>{link.label}</a>
                          )}
                        </span>
                      );
                    })}
                    .
                  </p>
                </div>
              )}

              <div className="stream-platforms">
                <h2>{platformHeading.before}<span className="highlight">{platformHeading.highlight}</span></h2>
                <div className="platform-links">
                  <a href="https://twitch.tv/zemaxi" target="_blank" rel="noopener noreferrer" className="platform-btn twitch-btn">
                    <i className="fab fa-twitch"></i> Twitch
                    <span className="platform-desc">Interactive Experience</span>
                  </a>
                  <a href="https://youtube.com/@zemaxi/live" target="_blank" rel="noopener noreferrer" className="platform-btn youtube-btn">
                    <i className="fab fa-youtube"></i> YouTube
                    <span className="platform-desc">1440p Pure Gameplay</span>
                  </a>
                </div>
              </div>

              <nav className="stream-sub-links" aria-label="Stream sections">
                <h2>Explore <span className="highlight">Stream</span></h2>
                <div className="stream-sub-links-grid">
                  <Link to={streamPagePath('schedule')} className="stream-sub-link-card">
                    <i className="fas fa-calendar" aria-hidden="true" />
                    <span>Schedule</span>
                  </Link>
                  <Link to={streamPagePath('info')} className="stream-sub-link-card">
                    <i className="fas fa-info-circle" aria-hidden="true" />
                    <span>Stream Info</span>
                  </Link>
                  <Link to={streamPagePath('setup')} className="stream-sub-link-card">
                    <i className="fas fa-desktop" aria-hidden="true" />
                    <span>Stream Setup</span>
                  </Link>
                </div>
              </nav>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
